import { apiRequest } from './api';
import { Company, CompanyWithDocuments } from '../store/company/types';

/**
 * Search for companies by name or ticker
 * @param query Search query
 * @returns Array of matching companies
 */
export const searchCompanies = async (
  query: string,
  options: { embeddedOnly?: boolean } = {},
): Promise<Company[]> => {
  return apiRequest<Company[]>('/companies/search', {
    params: options.embeddedOnly ? { query, embeddedOnly: 'true' } : { query },
  });
};

/**
 * Search for companies by ticker prefix
 * @param query Search query
 * @returns Array of matching companies
 */
export const searchCompaniesByTickerPrefix = async (
  query: string,
  options: { embeddedOnly?: boolean } = {},
): Promise<Company[]> => {
  return apiRequest<Company[]>('/companies/search-by-ticker-prefix', {
    params: options.embeddedOnly ? { query, embeddedOnly: 'true' } : { query },
  });
};

/**
 * Get a company by its exact ticker symbol
 * @param ticker Ticker symbol
 * @returns Company object or null if not found
 */
export const getCompanyByTicker = async (
  ticker: string,
  options: { embeddedOnly?: boolean } = {},
): Promise<Company | null> => {
  try {
    return await apiRequest<Company>('/companies/by-ticker', {
      params: options.embeddedOnly ? { ticker, embeddedOnly: 'true' } : { ticker },
    });
  } catch (error) {
    if ((error as any)?.status === 404) {
      return null;
    }
    throw error;
  }
};

/** * Get companies by IDs
 * @param companyIds Array of company IDs
 * @returns Array of companies
 */
export const getCompaniesByIds = async (companyIds: string[]): Promise<Company[]> => {
  return apiRequest<Company[]>('/companies/by-ids', {
    params: {
      ids: companyIds.join(','),
    },
  });
};

/**
 * Get companies with their documents
 * @param companyIds Array of company IDs
 * @returns Array of companies with their documents
 */
export const getCompaniesWithDocuments = async (
  companyIds: string[],
  options: { sort_by?: string; sort_dir?: string; type?: string } = {},
): Promise<CompanyWithDocuments[]> => {
  return apiRequest<CompanyWithDocuments[]>('/companies/with-documents', {
    params: {
      ids: companyIds.join(','),
      ...options,
    },
  });
};
